import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@/App";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ResponsiveBreadcrumb } from "@/components/breadcrumb";
import { MapPin, Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";

interface Region {
  id: number;
  name: string;
  description?: string;
  organization_id: number;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface Organization {
  id: number;
  name: string;
}

const regionSchema = z.object({
  name: z.string().min(1, "Region name is required"),
  description: z.string().optional(),
  organization_id: z.number(),
  display_order: z.number().optional(),
});

type RegionFormData = z.infer<typeof regionSchema>;

export function RegionsPage() {
  const { toast } = useToast();
  const { session } = useSession();
  const user = session?.user;
  
  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>(
    user?.role === 'OrgManager' || user?.role === 'OrgAdmin' ? user?.organization_id ?? undefined : undefined
  );
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [regionToDelete, setRegionToDelete] = useState<Region | null>(null);

  const canManageRegions = user?.role === 'Developer' || user?.role === 'PlatformManager' || user?.role === 'OrgManager';
  const canViewAllOrgs = user?.role === 'Developer' || user?.role === 'PlatformManager';

  // Fetch organizations (for Developer/PlatformManager)
  const { data: organizations = [] } = useQuery<Organization[]>({
    queryKey: ['/api/organizations'],
    enabled: canViewAllOrgs,
  });

  // Fetch regions
  const { data: regions = [], isLoading: regionsLoading } = useQuery<Region[]>({
    queryKey: ['/api/regions', selectedOrgId],
    queryFn: async () => {
      if (!selectedOrgId) return [];
      const response = await fetch(`/api/regions?organization_id=${selectedOrgId}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch regions');
      return response.json();
    },
    enabled: !!selectedOrgId,
  });

  // Create form
  const createForm = useForm<RegionFormData>({
    resolver: zodResolver(regionSchema),
    defaultValues: {
      name: "",
      description: "",
      organization_id: selectedOrgId,
      display_order: 0,
    },
  });

  // Sync organization_id in createForm when selectedOrgId changes
  useEffect(() => {
    if (selectedOrgId) {
      createForm.setValue("organization_id", selectedOrgId);
    }
  }, [selectedOrgId, createForm]);

  // Edit form
  const editForm = useForm<RegionFormData>({
    resolver: zodResolver(regionSchema),
    values: editingRegion ? {
      name: editingRegion.name,
      description: editingRegion.description || "",
      organization_id: editingRegion.organization_id,
      display_order: editingRegion.display_order,
    } : undefined,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: RegionFormData) => {
      const response = await apiRequest("POST", "/api/regions", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create region");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Region created",
        description: "The region has been created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/regions'] });
      setIsCreateDialogOpen(false);
      // Reset form with organization_id preserved
      createForm.reset({
        name: "",
        description: "",
        organization_id: selectedOrgId!,
        display_order: 0,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Creation failed",
        description: error.message || "Failed to create region",
        variant: "destructive",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: RegionFormData) => {
      if (!editingRegion) throw new Error("No region selected");
      const response = await apiRequest("PATCH", `/api/regions/${editingRegion.id}`, data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update region");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Region updated",
        description: "The region has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/regions'] });
      setIsEditDialogOpen(false);
      setEditingRegion(null);
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error.message || "Failed to update region",
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (regionId: number) => {
      const response = await apiRequest("DELETE", `/api/regions/${regionId}`);
      if (!response.ok) {
        const error = await response.json();
        if (error.code === 'REGION_IN_USE') {
          throw new Error("Cannot delete region with assigned homes. Please reassign homes first.");
        }
        throw new Error(error.message || "Failed to delete region");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Region deleted",
        description: "The region has been deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/regions'] });
      setRegionToDelete(null);
    },
    onError: (error: any) => {
      toast({
        title: "Deletion failed",
        description: error.message || "Failed to delete region",
        variant: "destructive",
      });
    },
  });

  const handleCreate = (data: RegionFormData) => {
    createMutation.mutate({
      ...data,
      organization_id: selectedOrgId!,
    });
  };

  const handleEdit = (data: RegionFormData) => {
    updateMutation.mutate(data);
  };

  const handleDelete = () => {
    if (regionToDelete) {
      deleteMutation.mutate(regionToDelete.id);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <ResponsiveBreadcrumb items={[
        { label: "Home", href: "/dashboard" },
        { label: "Regions Management" }
      ]} />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="page-title text-3xl font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="h-8 w-8 text-primary" />
              Regions Management
            </h1>
            <p className="text-slate-600 mt-2">
              Organize care homes into regional divisions for better management
            </p>
          </div>
        </div>

        {!canManageRegions && user?.role === 'OrgAdmin' && (
          <Alert className="mb-6">
            <AlertDescription>
              You have read-only access to regions. Contact your organization manager to make changes.
            </AlertDescription>
          </Alert>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Select Organization</CardTitle>
            <CardDescription>
              {canViewAllOrgs 
                ? "Select an organization to view and manage its regions"
                : "Viewing regions for your organization"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {canViewAllOrgs ? (
                <Select
                  value={selectedOrgId?.toString()}
                  onValueChange={(value) => setSelectedOrgId(parseInt(value))}
                  data-testid="select-organization"
                >
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Select an organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id.toString()}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-slate-600">
                  Organization: <span className="font-medium">{user?.organization_id}</span>
                </p>
              )}
              
              {canManageRegions && selectedOrgId && (
                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="button-create-region">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Region
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Region</DialogTitle>
                      <DialogDescription>
                        Create a new regional division to organize your care homes
                      </DialogDescription>
                    </DialogHeader>
                    <Form {...createForm}>
                      <form onSubmit={createForm.handleSubmit(handleCreate)} className="space-y-4">
                        <FormField
                          control={createForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Region Name</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="e.g., North Region, Scotland Division"
                                  data-testid="input-region-name"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={createForm.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Description (Optional)</FormLabel>
                              <FormControl>
                                <Textarea
                                  {...field}
                                  placeholder="Brief description of this region"
                                  data-testid="input-region-description"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={createForm.control}
                          name="display_order"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Display Order</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) => field.onChange(parseInt(e.target.value))}
                                  data-testid="input-display-order"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsCreateDialogOpen(false)}
                            data-testid="button-cancel-create"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={createMutation.isPending}
                            data-testid="button-submit-create"
                          >
                            {createMutation.isPending ? "Creating..." : "Create Region"}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardContent>
        </Card>

        {selectedOrgId && (
          <Card>
            <CardHeader>
              <CardTitle>Regions</CardTitle>
              <CardDescription>
                Regional divisions for organizing care homes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {regionsLoading ? (
                <p className="text-sm text-slate-500">Loading regions...</p>
              ) : regions.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 mb-4">No regions created yet</p>
                  {canManageRegions && (
                    <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="button-create-first-region">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Region
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {regions
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((region) => (
                      <div
                        key={region.id}
                        className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:border-primary transition-colors"
                        data-testid={`region-item-${region.id}`}
                      >
                        <GripVertical className="h-5 w-5 text-slate-400" />
                        <div className="flex-1">
                          <h3 className="font-medium text-slate-900" data-testid={`text-region-name-${region.id}`}>
                            {region.name}
                          </h3>
                          {region.description && (
                            <p className="text-sm text-slate-600 mt-1" data-testid={`text-region-description-${region.id}`}>
                              {region.description}
                            </p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            Order: {region.display_order}
                          </p>
                        </div>
                        {canManageRegions && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingRegion(region);
                                setIsEditDialogOpen(true);
                              }}
                              data-testid={`button-edit-region-${region.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setRegionToDelete(region)}
                              data-testid={`button-delete-region-${region.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Region</DialogTitle>
              <DialogDescription>
                Update the regional division details
              </DialogDescription>
            </DialogHeader>
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(handleEdit)} className="space-y-4">
                <FormField
                  control={editForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Region Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., North Region, Scotland Division"
                          data-testid="input-edit-region-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Brief description of this region"
                          data-testid="input-edit-region-description"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="display_order"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Order</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          data-testid="input-edit-display-order"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditDialogOpen(false);
                      setEditingRegion(null);
                    }}
                    data-testid="button-cancel-edit"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateMutation.isPending}
                    data-testid="button-submit-edit"
                  >
                    {updateMutation.isPending ? "Updating..." : "Update Region"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!regionToDelete} onOpenChange={(open) => !open && setRegionToDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Region</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{regionToDelete?.name}"? This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <Alert className="mb-4">
              <AlertDescription>
                You can only delete regions that have no care homes assigned. Please reassign all homes before deleting.
              </AlertDescription>
            </Alert>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setRegionToDelete(null)}
                data-testid="button-cancel-delete"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                data-testid="button-confirm-delete"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete Region"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
